import { AnimatedSwitch, Button, Match } from "@9amhealth/shared";
import { AnimatePresence, motion } from "framer-motion";
import { useBlocNext } from "@blac/react";
import Translate from "components/Translate/Translate";
import type { FC } from "react";
import { useEffect } from "react";
import OnboardingBloc from "src/state/OnboardingBloc/OnboardingBloc";
import FirstStep from "./FirstStep";
import InitialLabsBloc, { InitialLabsStep } from "./InitialLabsBloc";
import MedicalInsuranceStep from "./MedicalInsuranceStep";
import InitialLabsQuestionnaireStep from "./InitialLabsQuestionnaireStep";
import SkipLabsOptionStep from "./SkipLabsOptionStep";
import StatusStep from "./StatusStep";
import WaitingForFileBeingReviewed from "./WaitingForFileBeingReviewed";
import WaitingForLabRequisitionStep from "./WaitingForLabRequisitionStep";
import styled from "@emotion/styled";
import { APP_CONTENT_WIDTH_WITHOUT_PADDING } from "src/constants/layout";
import LabStatusOverviewCubit from "../LabStatusOverview/LabStatusOverviewCubit";

const FlowContainer = styled.div`
  label: FlowContainer;
  display: flex;
  flex-direction: column;
  width: 100%;
  background-color: var(--color-cream);
  padding-bottom: 0 !important;
  position: relative;
  min-height: 100%;
  z-index: 10;

  &[data-fill-height="true"] {
    height: 100%;
  }
`;

const AnimatedSwitchContainer = styled.div`
  width: 100%;
  min-height: 100%;

  &[data-fill-height="true"] {
    flex: 1;
    height: 100%;
  }

  &[data-fill-height="true"] > * {
    height: 100%;
    min-height: 100%;
  }
`;

const StepContainer = styled.div`
  label: StepContainer;
  background-color: var(--color-cream);
  padding-bottom: var(--padding-bottom, 0);
  min-height: 100%;

  &[data-fill-height="true"] {
    height: 100%;
  }
`;

const StepContainerCenterContent = styled(StepContainer)`
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  text-align: center;
`;
const FloatingButtonContainer = styled(motion.div)`
  label: FixedButtonContainer;
  position: fixed;
  bottom: 0;
  --break-out-horizontal: -8px;
  left: var(--break-out-horizontal);
  right: var(--break-out-horizontal);
  display: flex;
  justify-content: center;
  padding: var(--space-md);
  z-index: 80;

  .continue-button {
    width: min(100%, ${APP_CONTENT_WIDTH_WITHOUT_PADDING}px);
  }
`;

const InitialLabsFlow: FC = () => {
  const [, { handleInitialLabQuestionnaireCompleted }] =
    useBlocNext(OnboardingBloc);
  const [
    state,
    {
      checkForInitialLabTaskUpdates,
      nextStep,
      skipInitialLabs,
      showFloatingContinueButton,
      completeCurrentStep,
      handleQuestionnaireSubmit,
      handleMedicalInsuranceSaved
    }
  ] = useBlocNext(InitialLabsBloc);
  const shouldFillHeight =
    state.activeStep === InitialLabsStep.WaitingForFileBeingReviewed;

  const [, { fetchLabStatus }] = useBlocNext(LabStatusOverviewCubit);

  useEffect(() => {
    void checkForInitialLabTaskUpdates();
  }, [checkForInitialLabTaskUpdates]);

  if (!state.stepInitialized) {
    return null;
  }

  return (
    <>
      <FlowContainer
        data-fill-height={shouldFillHeight ? "true" : undefined}
        style={
          {
            "--padding-bottom": showFloatingContinueButton ? "2rem" : "0"
          } as React.CSSProperties
        }
      >
        <AnimatedSwitchContainer
          data-fill-height={shouldFillHeight ? "true" : undefined}
        >
          <AnimatedSwitch
            value={state.activeStep}
            scrollOnChange
            fillHeight={shouldFillHeight}
          >
            <Match case={InitialLabsStep.FirstStep}>
              <StepContainer>
                <FirstStep onContinue={nextStep} />
              </StepContainer>
            </Match>

            <Match
              case={InitialLabsStep.Status}
              preload={() => void fetchLabStatus()}
            >
              <StepContainer>
                <StatusStep />
              </StepContainer>
            </Match>

            <Match case={InitialLabsStep.SkipLabsOption}>
              <StepContainerCenterContent>
                <SkipLabsOptionStep
                  userCanSkip={state.canSkipInitialLabsButton}
                  onChooseLabs={nextStep}
                  onSkipLabs={() => void skipInitialLabs()}
                />
              </StepContainerCenterContent>
            </Match>

            <Match case={InitialLabsStep.Questionnaire}>
              <StepContainerCenterContent>
                <InitialLabsQuestionnaireStep
                  onDataSent={() =>
                    void handleInitialLabQuestionnaireCompleted()
                  }
                  onLastStepCompleted={handleQuestionnaireSubmit}
                />
              </StepContainerCenterContent>
            </Match>

            <Match case={InitialLabsStep.MedicalInsurance}>
              <MedicalInsuranceStep onSaved={handleMedicalInsuranceSaved} />
            </Match>

            <Match case={InitialLabsStep.WaitingForFileBeingReviewed}>
              <StepContainer data-fill-height="true">
                <WaitingForFileBeingReviewed />
              </StepContainer>
            </Match>

            <Match case={InitialLabsStep.WaitingForLabRequisition}>
              <WaitingForLabRequisitionStep />
            </Match>
          </AnimatedSwitch>
        </AnimatedSwitchContainer>
      </FlowContainer>

      <AnimatePresence>
        {showFloatingContinueButton && (
          <FloatingButtonContainer
            initial={{ y: "100%", opacity: 0 }}
            animate={{ y: 0, opacity: 1 }}
            exit={{ y: "100%", opacity: 0 }}
            transition={{ duration: 0.4, ease: "circOut" }}
          >
            <Button
              className="continue-button"
              onPress={completeCurrentStep}
              theme="sunrise"
              grow
              isDisabled={state.uploadReady === false}
            >
              <Translate msg="next" />
            </Button>
          </FloatingButtonContainer>
        )}
      </AnimatePresence>
    </>
  );
};

export default InitialLabsFlow;
